import React, { useEffect, useRef, useState } from "react";
import { Card, Typography } from "@mui/material";
import {
  SwipeableList,
  SwipeableListItem,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";
import trashbin from '../../../../../assets/Image/trash-bin.png';
import useFormattingUtils from "../../../../../context/useFormattingUtils";

function ProductContainer({
  products,
  setProducts,
  isFullscreen,
  onFieldSelect,
  onFieldChange,
  selectedField,
  onProductDelete,
}) {
  const [deleteStates, setDeleteStates] = useState({});
  const [animatingOut, setAnimatingOut] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const priceInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const { formatCurrency } = useFormattingUtils();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatTwoDecimals = (num) => {
    const parsed = parseFloat(num);
    return isNaN(parsed) ? "0.00" : parsed.toFixed(2);
  };

  useEffect(() => {
    if (selectedField.fieldType === "price" && priceInputRef.current) {
      priceInputRef.current.focus();
    } else if (
      selectedField.fieldType === "quantity" &&
      quantityInputRef.current
    ) {
      quantityInputRef.current.focus();
    }
  }, [selectedField]);

  useEffect(() => {
    const productsWithIds = products.map((product, index) => {
      if (!product.uniqueId) {
        return {
          ...product,
          uniqueId: `${product.title}-${product.price}-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
      }
      return product;
    });

    if (JSON.stringify(productsWithIds) !== JSON.stringify(products)) {
      setProducts(productsWithIds);
    }
  }, [products.length, products]);

  useEffect(() => {
    if (products && products.length > 0) {
      const validatedProducts = products.filter(product =>
        product &&
        product.title &&
        typeof product.price !== 'undefined' &&
        typeof product.quantity !== 'undefined'
      );

      if (validatedProducts.length !== products.length) {
        console.warn('Some products were filtered out due to invalid data');
        setProducts(validatedProducts);
      }
    }
  }, [products]);

  const handleDeleteProduct = (uniqueId) => {
    setAnimatingOut(uniqueId);
    setTimeout(() => {
      if (onProductDelete) {
        onProductDelete(uniqueId);
      } else {
        const updatedProducts = products.filter(
          (product) => product.uniqueId !== uniqueId
        );
        setProducts(updatedProducts);
      }

      setAnimatingOut(null);
      const newDeleteStates = { ...deleteStates };
      delete newDeleteStates[uniqueId];
      setDeleteStates(newDeleteStates);
    }, 300);
  };

  const toggleDeleteMode = (uniqueId) => {
    setDeleteStates(prev => ({
      ...prev,
      [uniqueId]: !prev[uniqueId]
    }));
  };

  const handleInputChange = (productIndex, fieldType, value) => {
    if (fieldType === "quantity") {
      const cleanedValue = value.replace(/^x\s*/, "").trim();

      if (cleanedValue === "") {
        onFieldChange(productIndex, fieldType, "");
        return;
      }

      if (!/^\d*\.?\d+$/.test(cleanedValue)) {
        return;
      }

      // When quantity changes, calculate new total based on unit price
      const product = products[productIndex];
      if (product && product.price !== undefined) {
        const unitPrice = parseFloat(product.price);
        const newQuantity = parseInt(cleanedValue) || 1;
        const newTotal = unitPrice * newQuantity;
        
        // Update both quantity and tempTotalPrice
        setProducts(prev => {
          const updated = [...prev];
          updated[productIndex] = {
            ...updated[productIndex],
            quantity: newQuantity,
            tempTotalPrice: newTotal.toFixed(2)
          };
          return updated;
        });
      } else {
        onFieldChange(productIndex, fieldType, cleanedValue);
      }
    } else if (fieldType === "price") {
      const sanitizedValue = value
        .replace(/[^0-9.-]/g, "")
        .replace(/(\..*)\./g, "$1")
        .replace(/^(-)?(\d*\.?\d{0,2}).*$/, "$1$2");

      // When price changes, treat it as a total price input
      const product = products[productIndex];
      if (product && product.quantity !== undefined) {
        const quantity = parseInt(product.quantity) || 1;
        const newTotalPrice = parseFloat(sanitizedValue) || 0;
        const newUnitPrice = newTotalPrice / quantity;
        
        // Update both price and tempTotalPrice
        setProducts(prev => {
          const updated = [...prev];
          updated[productIndex] = {
            ...updated[productIndex],
            price: newUnitPrice.toFixed(2),
            tempTotalPrice: sanitizedValue
          };
          return updated;
        });
      } else {
        onFieldChange(productIndex, fieldType, sanitizedValue);
      }
    }
  };

  const getTotalQuantity = () => {
    return products.reduce(
      (total, product) => {
        const price = parseFloat(product.price) || 0;
        const quantity = parseInt(product.quantity) || 0;

        if (price >= 0) {
          return total + quantity;
        }
        return total;
      },
      0
    );
  };

  const getTotalPrice = () => {
    const total = products.reduce((total, product) => {
      if (product.tempTotalPrice !== undefined) {
        return total + (parseFloat(product.tempTotalPrice) || 0);
      }
      const price = parseFloat(product.price) || 0;
      const quantity = parseInt(product.quantity || 0);

      const productTotal = Math.round((price * quantity) * 100) / 100;
      return total + productTotal;
    }, 0);

    const clampedTotal = Math.max(0, total);
    return clampedTotal.toFixed(2);
  };

  const containerStyles = {
    width: "100%",
    maxWidth: isMobile ? "100%" : "410px",
    height: isFullscreen ? (isMobile ? "400px" : "510px") : (isMobile ? "280px" : "330px"),
    borderRadius: isMobile ? "15px" : "30px",
    border: isMobile ? "1px solid #ddd" : "2px solid #000000",
    background: "#FFFFFF",
    padding: isMobile ? "8px" : "10px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    margin: "0 auto",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#888 white",
  };

  const productCardStyles = {
    background: "white",
    color: "#000",
    borderBottom: "1px solid #f0f0f0",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    position: "relative",
    marginBottom: isMobile ? "4px" : "2px",
    borderRadius: isMobile ? "8px" : "4px",
    boxShadow: isMobile ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
  };

  const renderMobileLayout = (product, index) => (
    <div style={{ padding: "10px", position: "relative" }}>
      {deleteStates[product.uniqueId] && (
        <div
          style={{
            position: "absolute",
            right: "8px",
            top: "8px",
            width: "32px",
            height: "32px",
            background: "#ff4747",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
          onClick={() => handleDeleteProduct(product.uniqueId)}
        >
          <img
            src={trashbin}
            alt="Delete"
            style={{ width: "18px", height: "18px" }}
          />
        </div>
      )}

      <div onClick={() => toggleDeleteMode(product.uniqueId)}>
        <Typography
          style={{
            color: "#000",
            fontSize: "15px",
            fontWeight: "500",
            marginBottom: "10px",
            paddingRight: deleteStates[product.uniqueId] ? "45px" : "0",
            lineHeight: "1.3",
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {product.title}
        </Typography>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <div style={{ flex: "0 0 auto" }}>
            <input
              type="text"
              value={`x ${product.quantity}`}
              onClick={(e) => {
                e.stopPropagation();
                onFieldSelect(index, "quantity");
              }}
              onChange={(e) => {
                const value = e.target.value;
                const numericValue = value.replace(/\D/g, '');
                if (numericValue.length <= 3) {
                  handleInputChange(index, "quantity", numericValue);
                }
              }}
              style={{
                width: "70px",
                padding: "8px 6px",
                fontSize: "14px",
                backgroundColor: "#F9F9F9",
                borderRadius: "6px",
                border: "1px solid #ddd",
                color: "#000",
                fontWeight: "500",
                textAlign: "center",
              }}
              ref={
                index === selectedField.productIndex &&
                  selectedField.fieldType === "quantity"
                  ? quantityInputRef
                  : null
              }
            />
          </div>
          <div style={{ flex: "1", minWidth: "100px", position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "14px",
                color: "#000",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              {formatCurrency(0).replace(/0.00/g, '')}
            </span>
            <input
              type="text"
              value={
                product.tempTotalPrice !== undefined
                  ? product.tempTotalPrice
                  : formatTwoDecimals(product.price * (product.quantity || 1))
              }
              onClick={(e) => {
                e.stopPropagation();
                onFieldSelect(index, "price");
              }}
              onChange={(e) => {
                const inputValue = e.target.value;
                let sanitizedValue = inputValue
                  .replace(/[^\d.-]/g, '')
                  .replace(/(?!^)-/g, '')
                  .replace(/(\..*)\./g, '$1');
                
                const parts = sanitizedValue.split('.');
                if (parts.length > 1) {
                  sanitizedValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
                }
                handleInputChange(index, "price", sanitizedValue);
              }}
              style={{
                width: "100%",
                padding: "8px 6px",
                paddingLeft: "22px",
                fontSize: "14px",
                backgroundColor: "#F9F9F9",
                borderRadius: "6px",
                border: "1px solid #ddd",
                color: (parseFloat(product.price) || 0) < 0 ? "red" : "#000",
                fontWeight: "500",
              }}
              ref={
                index === selectedField.productIndex &&
                  selectedField.fieldType === "price"
                  ? priceInputRef
                  : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderDesktopLayout = (product, index) => (
    <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
      {deleteStates[product.uniqueId] && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "30px",
            background: "#ff4747",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            cursor: "pointer",
          }}
          onClick={() => handleDeleteProduct(product.uniqueId)}
        >
          <img
            src={trashbin}
            alt="Delete"
            style={{ width: "24px", height: "24px" }}
          />
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 90px 90px",
          alignItems: "center",
          padding: "8px 0px",
          width: deleteStates[product.uniqueId] ? "calc(100% - 30px)" : "100%",
          transition: "width 0.3s ease",
          gap: "12px",
        }}
        onClick={() => toggleDeleteMode(product.uniqueId)}
      >
        <Typography
          style={{
            color: "#000",
            fontSize: "16px",
            fontWeight: "500",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.4",
          }}
        >
          {product.title}
        </Typography>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <input
            type="text"
            value={`x ${product.quantity}`}
            onClick={(e) => {
              e.stopPropagation();
              onFieldSelect(index, "quantity");
            }}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue = value.replace(/\D/g, '');
              if (numericValue.length <= 3) {
                handleInputChange(index, "quantity", numericValue);
              }
            }}
            style={{
              width: "90px",
              textAlign: "center",
              padding: "8px",
              backgroundColor: "#F9F9F9",
              color: "#000",
              borderRadius: "6px",
              border: "1px solid #ddd",
              fontSize: "14px",
              fontWeight: "500",
            }}
            ref={
              index === selectedField.productIndex &&
                selectedField.fieldType === "quantity"
                ? quantityInputRef
                : null
            }
          />
        </div>

        <div style={{ position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#000",
              fontSize: "14px",
              pointerEvents: "none",
            }}
          >
            {formatCurrency(0).replace(/0.00/g, '')}
          </span>
          <input
            type="text"
            value={
              product.tempTotalPrice !== undefined
                ? product.tempTotalPrice
                : formatTwoDecimals(product.price * (product.quantity || 1))
            }
            onClick={(e) => {
              e.stopPropagation();
              onFieldSelect(index, "price");
            }}
            onChange={(e) => {
              const inputValue = e.target.value;
              const sanitizedValue = inputValue
                .replace(/[^\d.-]/g, "")
                .replace(/(?!^)-/g, "")
                .replace(/(\..*)\./g, "$1");

              const digitCount = sanitizedValue.replace(/[^0-9]/g, "").length;
              if (digitCount <= 5) {
                handleInputChange(index, "price", sanitizedValue);
              }
            }}
            style={{
              width: "90px",
              textAlign: "start",
              padding: "8px",
              backgroundColor: "#F9F9F9",
              paddingLeft: "24px",
              color: (parseFloat(product.price) || 0) < 0 ? "red" : "#000",
              borderRadius: "6px",
              border: "1px solid #ddd",
              fontSize: "14px",
              fontWeight: "500",
            }}
            ref={
              index === selectedField.productIndex &&
                selectedField.fieldType === "price"
                ? priceInputRef
                : null
            }
          />
        </div>
      </div>
    </div>
  );

  return (
    <div style={containerStyles}>
      {products.length > 0 ? (
        <SwipeableList>
          {products.map((product, index) => (
            <div
              key={product.uniqueId || index}
              style={{
                transition: "transform 0.3s ease, opacity 0.3s ease",
                transform: animatingOut === product.uniqueId ? "translateX(-100%)" : "translateX(0)",
                opacity: animatingOut === product.uniqueId ? 0 : 1,
              }}
            >
              <SwipeableListItem>
                <Card sx={productCardStyles}>
                  {isMobile ? renderMobileLayout(product, index) : renderDesktopLayout(product, index)}
                </Card>
              </SwipeableListItem>
            </div>
          ))}
        </SwipeableList>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "#666",
            textAlign: "center",
          }}
        >
          <Typography variant="body1" style={{ fontSize: isMobile ? "16px" : "18px" }}>
            No products selected
          </Typography>
          <Typography
            variant="body2"
            style={{
              marginTop: "10px",
              fontSize: isMobile ? "14px" : "16px",
              color: "#888"
            }}
          >
            Please add new products from the Cart
          </Typography>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "16px",
          paddingTop: "12px",
          borderTop: isMobile ? "1px solid #ddd" : "2px solid #000000",
          fontWeight: "bold",
          gap: isMobile ? "8px" : "20px",
          flexWrap: "nowrap",
        }}
      >
        <Typography
          variant="h6"
          style={{
            fontSize: isMobile ? "14px" : "18px",
            fontWeight: "900",
            color: "#000",
            whiteSpace: "nowrap",
            flex: "0 0 auto",
            
          }}
        >
          Total Quantity: {getTotalQuantity()}
        </Typography>
        <Typography
          variant="h6"
          style={{
            fontSize: isMobile ? "14px" : "18px",
            fontWeight: "900",
            color: parseFloat(getTotalPrice()) < 0 ? "red" : "#000",
            textAlign: "right",
            flex: "1 1 auto",
            minWidth: "0",
            overflow: "hidden",
            textOverflow: "ellipsis",
            
          }}
        >
          Total Price: {formatCurrency(getTotalPrice())}
        </Typography>
      </div>
    </div>
  );
}

export default ProductContainer;